import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, formatApiError } from "@/lib/api";
import { Plus, Search, KeyRound, Upload, FolderOpen } from "lucide-react";
import EmployeeFormModal from "@/components/EmployeeFormModal";
import ResetPasswordModal from "@/components/ResetPasswordModal";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";

export default function Employees() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef();

  const load = useCallback(async () => {
    const { data } = await api.get("/employees");
    setRows(data);
  }, []);
  useEffect(() => { if (user?.role === "admin") load(); }, [user, load]);

  if (user && user.role !== "admin") return <Navigate to="/app/portal" replace />;

  const filtered = rows.filter((r) => {
    const s = `${r.first_name} ${r.last_name} ${r.email}`.toLowerCase();
    return s.includes(q.toLowerCase());
  });

  const terminate = async (id) => {
    if (!window.confirm("Mark employee as terminated?")) return;
    await api.delete(`/employees/${id}`);
    toast.success("Employee terminated");
    load();
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/employees/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.created} · Skipped ${data.skipped}`);
      if (data.errors?.length) toast.error(data.errors.join(" | "));
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    setImporting(false);
    e.target.value = "";
  };

  const downloadSample = () => {
    const csv = "first_name,last_name,email,state,employment_type,hourly_rate,annual_salary,filing_status,address,city,zip_code\nAlex,Jones,alex@example.com,CA,hourly,32,0,single,10 Main St,San Francisco,94103\nSam,Lee,sam@example.com,TX,salary,0,75000,married,55 Oak Ave,Austin,73301\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "employees-sample.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 sm:p-10">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <div className="overline mb-2">§ ROSTER</div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight">Employees</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button data-testid="import-employees-btn" onClick={() => fileRef.current?.click()} disabled={importing} className="border-2 border-ink px-4 py-3 font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2 hover:bg-secondary disabled:opacity-50">
            <Upload className="w-4 h-4" /> {importing ? "Importing…" : "Import CSV / XLSX / PDF"}
          </button>
          <input ref={fileRef} type="file" accept=".csv,.xls,.xlsx,.pdf" data-testid="import-csv-file" onChange={handleImport} className="hidden" />
          <button onClick={downloadSample} className="text-xs font-mono underline underline-offset-4 text-muted-foreground">sample.csv</button>
          <button data-testid="add-employee-btn" onClick={() => { setEditing(null); setOpen(true); }} className="hard-btn px-6 py-3 font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> Add Employee
          </button>
        </div>
      </div>

      <div className="hard-card p-4 mb-6 flex items-center gap-3">
        <Search className="w-4 h-4" />
        <input data-testid="employee-search" placeholder="Search by name or email…" value={q} onChange={(e) => setQ(e.target.value)} className="flex-1 font-mono text-sm bg-transparent focus:outline-none" />
      </div>

      <div className="hard-card overflow-x-auto">
        <table className="w-full font-mono text-sm" data-testid="employees-table">
          <thead className="bg-ink text-white">
            <tr>
              <th className="text-left px-4 py-3 uppercase tracking-widest text-xs">Name</th>
              <th className="text-left px-4 py-3 uppercase tracking-widest text-xs">Email</th>
              <th className="text-left px-4 py-3 uppercase tracking-widest text-xs">Type</th>
              <th className="text-right px-4 py-3 uppercase tracking-widest text-xs">Rate</th>
              <th className="text-left px-4 py-3 uppercase tracking-widest text-xs">State</th>
              <th className="text-left px-4 py-3 uppercase tracking-widest text-xs">Pay Method</th>
              <th className="text-left px-4 py-3 uppercase tracking-widest text-xs">Status</th>
              <th className="text-right px-4 py-3 uppercase tracking-widest text-xs">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="text-center py-10 text-muted-foreground">No employees yet.</td></tr>
            )}
            {filtered.map((e, i) => (
              <tr key={e.id} className={i % 2 === 0 ? "bg-white" : "bg-[#F9F9F9]"} data-testid={`employee-row-${e.id}`}>
                <td className="px-4 py-3 border-t border-ink/20 font-semibold">
                  <Link
                    to={`/app/employees/${e.id}`}
                    data-testid={`view-employee-${e.id}`}
                    className="underline underline-offset-2 hover:text-ink/70"
                  >{e.first_name} {e.last_name}</Link>
                </td>
                <td className="px-4 py-3 border-t border-ink/20">{e.email}</td>
                <td className="px-4 py-3 border-t border-ink/20 uppercase">{e.employment_type}</td>
                <td className="px-4 py-3 border-t border-ink/20 text-right">{e.employment_type === "hourly" ? `${fmtMoney(e.hourly_rate)}/hr` : `${fmtMoney(e.annual_salary)}/yr`}</td>
                <td className="px-4 py-3 border-t border-ink/20">{e.state}</td>
                <td className="px-4 py-3 border-t border-ink/20">
                  {e.payment_method === "direct_deposit" ? (
                    <div className="flex flex-col leading-tight">
                      <span className="px-1.5 py-0.5 bg-good text-white text-[10px] uppercase tracking-widest w-fit">ACH</span>
                      <span className="text-[10px] text-muted-foreground mt-0.5">{e.bank_routing} · {e.bank_account_masked}</span>
                    </div>
                  ) : (
                    <span className="px-1.5 py-0.5 bg-muted text-ink text-[10px] uppercase tracking-widest">Check</span>
                  )}
                </td>
                <td className="px-4 py-3 border-t border-ink/20"><span className={`px-2 py-0.5 text-xs uppercase tracking-widest ${e.status === "active" ? "bg-good text-white" : "bg-muted text-ink"}`}>{e.status}</span></td>
                <td className="px-4 py-3 border-t border-ink/20 text-right space-x-2 whitespace-nowrap">
                  <Link
                    to={`/app/employees/${e.id}`}
                    data-testid={`personnel-file-${e.id}`}
                    className="inline-flex items-center gap-1 border border-ink px-2 py-1 text-xs uppercase tracking-widest hover:bg-ink hover:text-white"
                    title="Open personnel file (HR documents)"
                  ><FolderOpen className="w-3 h-3" /> File</Link>
                  <button onClick={() => { setEditing(e); setOpen(true); }} data-testid={`edit-employee-${e.id}`} className="border border-ink px-2 py-1 text-xs uppercase tracking-widest hover:bg-ink hover:text-white">Edit</button>
                  <button onClick={() => setResetting(e)} data-testid={`reset-password-${e.id}`} className="inline-flex items-center gap-1 border border-ink px-2 py-1 text-xs uppercase tracking-widest hover:bg-warn hover:border-warn"><KeyRound className="w-3 h-3" /> Reset PW</button>
                  {e.status === "active" && (
                    <button onClick={() => terminate(e.id)} data-testid={`terminate-employee-${e.id}`} className="border border-bad text-bad px-2 py-1 text-xs uppercase tracking-widest hover:bg-bad hover:text-white">Terminate</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <EmployeeFormModal
          employee={editing}
          onClose={() => setOpen(false)}
          onSaved={(saved) => {
            setOpen(false);
            if (!saved) { load(); return; }
            setRows((prev) => {
              const idx = prev.findIndex((r) => r.id === saved.id);
              if (idx === -1) return [...prev, saved];
              const next = prev.slice();
              next[idx] = saved;
              return next;
            });
          }}
        />
      )}
      {resetting && (
        <ResetPasswordModal employee={resetting} onClose={() => setResetting(null)} />
      )}
    </div>
  );
}
